import React from "react";
import { Button, CircularProgress, styled } from "@mui/material";
import { fontWeight } from "../theme/fontWeight";
import { fontSize } from "../theme/fontSize";
import { FontFamily } from "../theme/fontFamily";
import { darken } from "@mui/material/styles";
import { ThemeColor } from "../theme/color";

const sidebarGradient = "linear-gradient(180deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%)";

// keep the styling-only props out of the DOM (React warns about unknown attributes)
const isStyleProp = (prop) =>
  ["customColor", "textcolor", "padding"].includes(prop);

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => !isStyleProp(prop),
})(
  ({ theme, customColor, textcolor, padding }) => ({
    borderRadius: "10px",
    padding: "8px 20px",
    fontWeight: fontWeight.W600,
    fontSize: fontSize.h3.medium,
    fontFamily: FontFamily.Nunito,
    width: 'auto',
    textTransform: "none",
    boxShadow: "0 6px 18px rgba(16,24,40,0.08)",
    transition: 'transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease',
    // Ensure the contained variant uses the gradient (higher specificity)
    '&.MuiButton-contained': {
      background: customColor || sidebarGradient,
      color: textcolor || '#ffffff',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 10px 24px rgba(16,24,40,0.12)',
        filter: customColor ? undefined : 'brightness(0.95)'
      },
      '&.Mui-disabled': {
        opacity: 0.6,
        boxShadow: 'none',
        filter: 'grayscale(0.2)'
      }
    },
    // For outlined variant keep transparent background but a subtle gradient border
    '&.MuiButton-outlined': {
      background: 'transparent',
      color: textcolor || theme.palette.primary.main,
      border: '1px solid transparent',
      borderImage: `linear-gradient(180deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%) 1`,
      '&:hover': {
        background: theme.palette.action.hover
      }
    }
  })
);

const CustomButton = ({
  variant = "contained",
  color,
  onClick,
  children,
  type,
  backgroundColor,
  loading,
  // The styled button already has a `&.Mui-disabled` rule, but the prop was
  // never forwarded, so it silently did nothing.
  disabled,
}) => {
  return (
    <StyledButton
      textcolor={color}
      variant={variant}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      customColor={backgroundColor==ThemeColor.PURPLE?null:backgroundColor}
    >
      {loading ? (
        <div className="d-flex align-items-center">
          {children}{" "}
          <div className="px-2 d-flex align-items-center">
            <CircularProgress size={24} color="inherit" className="" />
          </div>
        </div>
      ) : (
        children
      )}
    </StyledButton>
  );
};

export default CustomButton;
